import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Show } from "@clerk/react";
import { ArrowRight, UserPlus, QrCode, BadgeCheck, Coins, Network } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { API, REF_KEY, inr } from "@/lib/api";
import { Logo } from "@/components/Logo";

const Nav = () => (
  <header className="sticky top-0 z-40 border-b border-[#1F293D]/70 bg-[#0B0F17]/70 backdrop-blur-md">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8">
      <Logo />
      <nav className="hidden items-center gap-8 text-sm text-slate-400 md:flex">
        <a href="#how" className="hover:text-white" data-testid="nav-how-link">How it works</a>
        <a href="#plan" className="hover:text-white" data-testid="nav-plan-link">Plan</a>
      </nav>
      <div className="flex items-center gap-3">
        <Link to="/sign-in" data-testid="nav-login-btn" className="rounded-full px-4 py-2 text-sm text-slate-300 hover:text-white">Login</Link>
        <Link to="/sign-up" data-testid="nav-register-btn" className="rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-[#0B0F17] transition-colors hover:bg-emerald-400">Join now</Link>
      </div>
    </div>
  </header>
);

const Calculator = ({ s }) => {
  const [n, setN] = useState([10]);
  return (
    <div data-testid="earnings-calculator" className="rounded-2xl border border-[#1F293D] bg-[#111827]/80 p-6 backdrop-blur-xl">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-500">Referral calculator</p>
      <div className="mt-6 flex items-end justify-between">
        <div>
          <p className="text-sm text-slate-400">Direct members activated</p>
          <p className="font-display text-5xl font-extrabold text-white" data-testid="calculator-count">{n[0]}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-400">You earn</p>
          <p className="font-display text-4xl font-extrabold text-emerald-400" data-testid="calculator-earnings">{inr(n[0] * s.referral_bonus)}</p>
        </div>
      </div>
      <Slider data-testid="calculator-slider" className="mt-8" value={n} onValueChange={setN} min={1} max={100} step={1} />
      <p className="mt-6 text-xs text-slate-500">{inr(s.referral_bonus)} per activated direct referral. Illustration only — actual earnings depend on your referrals.</p>
    </div>
  );
};

const STEPS = [
  { icon: UserPlus, t: "Create account", d: "Sign up free with your email, using a sponsor's referral link." },
  { icon: QrCode, t: "Pay via UPI", d: "Scan the QR or pay the UPI ID the joining amount from any UPI app." },
  { icon: BadgeCheck, t: "Submit UTR", d: "Enter the 12-digit UTR. Admin verifies and activates your ID." },
  { icon: Coins, t: "Earn & withdraw", d: "Share your link. Each activated referral credits your wallet." },
];

export default function Landing() {
  const [params] = useSearchParams();
  const [s, setS] = useState({ package_price: 2000, referral_bonus: 500 });
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) localStorage.setItem(REF_KEY, ref.toUpperCase());
    axios.get(`${API}/settings`).then((r) => setS(r.data)).catch(() => {});
  }, [params]);

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200">
      <Show when="signed-in"><Navigate to="/dashboard" replace /></Show>
      <Nav />
      <section className="grain grid-lines relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-8 lg:grid-cols-12 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500">Unilevel network · India</p>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Join for <span className="text-emerald-400">{inr(s.package_price)}</span>.<br />Earn <span className="text-amber-400">{inr(s.referral_bonus)}</span> on every member you bring.
            </h1>
            <p className="mt-6 max-w-xl text-base text-slate-400">A transparent referral network. Pay once via UPI, get verified within hours, and grow an unlimited direct team — every activation is credited straight to your wallet.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/sign-up" data-testid="hero-join-btn" className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 font-semibold text-[#0B0F17] transition-colors hover:bg-emerald-400">
                Start your network <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="#how" data-testid="hero-how-btn" className="rounded-full border border-[#1F293D] px-7 py-3.5 text-slate-300 transition-colors hover:border-slate-500 hover:text-white">How it works</a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="lg:col-span-5">
            <Calculator s={s} />
          </motion.div>
        </div>
      </section>

      <section id="how" className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500">How it works</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold text-white sm:text-4xl">Four steps from sign-up to payout.</h2>
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-[#1F293D] bg-[#1F293D] md:grid-cols-4">
          {STEPS.map((st, i) => (
            <div key={st.t} className="bg-[#0B0F17] p-8 transition-colors hover:bg-[#111827]" data-testid={`step-${i + 1}`}>
              <p className="font-mono text-xs text-slate-600">0{i + 1}</p>
              <st.icon className="mt-6 h-6 w-6 text-emerald-400" />
              <h3 className="mt-4 font-display text-xl font-bold text-white">{st.t}</h3>
              <p className="mt-2 text-sm text-slate-400">{st.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="plan" className="mx-auto grid max-w-7xl gap-6 px-4 pb-24 sm:px-8 lg:grid-cols-12">
        <div className="rounded-2xl border border-[#1F293D] bg-[#111827] p-8 lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500">The plan</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold text-white">One package. Three levels of bonus.</h2>
          <dl className="mt-8 divide-y divide-[#1F293D] text-sm">
            {[["Joining package", inr(s.package_price)], ["Direct referral bonus (L1)", inr(s.referral_bonus)], ["Level 2 bonus", inr(s.level2_bonus)], ["Level 3 bonus", inr(s.level3_bonus)], ["Direct referrals", "Unlimited"], ["Payment", "UPI + UTR verification"], ["Minimum withdrawal", inr(s.min_withdrawal)]].map(([k, v]) => (
              <div key={k} className="flex justify-between py-3"><dt className="text-slate-400">{k}</dt><dd className="font-mono text-white">{v}</dd></div>
            ))}
          </dl>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-[#1F293D] lg:col-span-7">
          <img src="https://images.pexels.com/photos/5583969/pexels-photo-5583969.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Network team" className="h-full min-h-[320px] w-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F17] via-[#0B0F17]/40 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <Network className="h-6 w-6 text-emerald-400" />
            <p className="mt-3 max-w-md font-display text-2xl font-bold text-white">Your whole downline, visible as a tree — see who's active and who needs a nudge.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#1F293D]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-4 py-10 text-sm text-slate-500 sm:flex-row sm:items-center sm:px-8">
          <Logo />
          <p data-testid="footer-copyright">© 2023 Indian Small Capital Microfinance</p>
        </div>
      </footer>
    </div>
  );
}
